/**
 * Loading skeletons for the proposal sidebar, one per region.
 *
 * - `ProposalHeaderSkeleton` — the header slot's CONTENT while the proposal
 *   loads: avatar + title stack, the same 72px row as `ProposalDetailHeader`.
 *   The chrome renders the real close button beside it, so there is no
 *   placeholder for one here.
 * - `ProposalBodySkeleton` — the scrollable body and the pinned footer, so
 *   the slideout doesn't visibly reflow when the real proposal replaces it.
 *
 * Same shared tokens (`bg-bg-secondary`, `bg-bg-tertiary`) and
 * `animate-pulse` rhythm as the CRM record skeletons so the two feel cohesive.
 */

export function ProposalHeaderSkeleton() {
  return (
    <div
      className='flex h-[72px] items-center gap-3 px-5'
      aria-busy='true'
      data-testid='proposal-header-skeleton'
    >
      <div className='size-8 shrink-0 animate-pulse rounded-full bg-[var(--color-bg-tertiary)]' />
      <div className='flex flex-1 flex-col gap-1.5'>
        <div className='h-4 w-3/5 animate-pulse rounded bg-[var(--color-bg-tertiary)]' />
        <div className='h-3 w-2/5 animate-pulse rounded bg-[var(--color-bg-tertiary)]' />
      </div>
    </div>
  );
}

export function ProposalBodySkeleton() {
  return (
    <div
      className='flex h-full w-full flex-col overflow-hidden bg-bg-secondary'
      role='status'
      aria-live='polite'
      aria-label='Loading action'
      data-testid='proposal-body-skeleton'
    >
      {/* Content skeleton — proposal body blocks */}
      <div className='flex-1 space-y-4 overflow-y-auto px-5 py-4'>
        {/* Description block */}
        <div className='space-y-2'>
          <div className='h-3 w-full animate-pulse rounded bg-[var(--color-bg-tertiary)]' />
          <div className='h-3 w-11/12 animate-pulse rounded bg-[var(--color-bg-tertiary)]' />
          <div className='h-3 w-3/4 animate-pulse rounded bg-[var(--color-bg-tertiary)]' />
        </div>

        {/* Card-shaped placeholder for the proposed action preview */}
        <div className='animate-pulse rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] p-4'>
          <div className='mb-3 h-4 w-1/3 rounded bg-[var(--color-bg-tertiary)]' />
          <div className='space-y-2'>
            <div className='h-3 w-full rounded bg-[var(--color-bg-tertiary)]' />
            <div className='h-3 w-5/6 rounded bg-[var(--color-bg-tertiary)]' />
            <div className='h-3 w-4/6 rounded bg-[var(--color-bg-tertiary)]' />
          </div>
        </div>

        {/* Secondary text block */}
        <div className='space-y-2'>
          <div className='h-3 w-1/2 animate-pulse rounded bg-[var(--color-bg-tertiary)]' />
          <div className='h-3 w-2/3 animate-pulse rounded bg-[var(--color-bg-tertiary)]' />
        </div>
      </div>

      {/* Footer skeleton — sticky action bar */}
      <div className='flex shrink-0 items-center justify-end gap-2 border-t border-[var(--color-border-secondary)] bg-bg-secondary px-5 py-3'>
        <div className='h-9 w-20 animate-pulse rounded-md bg-[var(--color-bg-tertiary)]' />
        <div className='h-9 w-24 animate-pulse rounded-md bg-[var(--color-bg-tertiary)]' />
      </div>

      <span className='sr-only'>Loading action…</span>
    </div>
  );
}
